import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { parseUnits } from 'viem';
import { environment } from 'src/environments/environment';
import { Erc20ContractService } from './erc20-contract.service';

/**
 * Which contract the allowance is granted to. Loan offers and counters are pulled by the
 * borrow-lend contract; NFT bids are pulled by the exchange. Each is sized and reduced on its own.
 */
export type AllowanceSpender = 'lending' | 'exchange';

const getSpenderAddress = (chainId: string | number, spender: AllowanceSpender = 'lending'): string =>
  (environment as any)[chainId as any]?.[spender === 'exchange' ? 'EXCHANGE_CONTRACT' : 'BORROW_LEND_CONTRACT'];

export interface AllowanceCheckResponse {
  activeCounterSum: string;
  contractAllowance: string;
}

export interface ComputeCounterAllowanceInput {
  account: { walletAddress: string; chainId: string; networkId: number };
  currencyId: string;
  currencyAddress: string;
  excludeId?: string;
  counterAmountWithFeesHuman: number;
  /**
   * Exact required amount in wei, read from the contract's own view functions.
   * When provided (> 0n) it is used verbatim instead of converting the human
   * amount via toWei — this is the precise on-chain pull, so it needs no float
   * buffer and leaves no exploitable residual allowance. The human amount stays
   * as a fallback for callers that cannot compute an exact figure.
   */
  counterAmountWithFeesWei?: bigint;
  /** Contract the allowance is for; defaults to the borrow-lend contract. */
  spender?: AllowanceSpender;
}

export type AllowanceAction = 'increase' | 'decrease' | 'none';

export interface ComputeExactAllowanceInput {
  account: { walletAddress: string; chainId: string; networkId: number };
  currencyAddress: string;
  /** Contract that pulls the payment: an NFT collection (custody fee, lazy mint) or the syndicate pool. */
  spenderAddress: string;
  /** Exactly what this payment pulls, in wei (a whole-number string or number is accepted too). */
  requiredWei: bigint | string | number;
}

export interface ComputeExactAllowanceResult {
  action: AllowanceAction;
  deltaWei: bigint;
  spenderAddress: string;
}

export interface ComputeCounterAllowanceResult {
  allowanceAmount: number;
  action: AllowanceAction;
  deltaWei: bigint;
  /** Address the increase or decrease must be sent for, matching the spender the figures used. */
  spenderAddress: string;
}

@Injectable({ providedIn: 'root' })
export class AllowanceService {
  private http = inject(HttpClient);
  private erc20ContractService = inject(Erc20ContractService);

  async checkAllowance(currencyId: string, excludeId?: string, spender: AllowanceSpender = 'lending'): Promise<AllowanceCheckResponse> {
    const body: { currency_id: string; exclude_id?: string; spender?: AllowanceSpender } = { currency_id: currencyId };
    if (excludeId) body.exclude_id = excludeId;
    // The backend defaults to lending, so the lending request stays exactly as it was.
    if (spender === 'exchange') body.spender = spender;
    const res: any = await firstValueFrom(
      this.http.post(`${environment.API_BASE_URL_V3}/allowance/check`, body)
    );
    const data = res?.data ?? {};
    return {
      activeCounterSum: String(data.activeCounterSum ?? '0'),
      contractAllowance: String(data.contractAllowance ?? '0')
    };
  }

  async computeCounterAllowance(input: ComputeCounterAllowanceInput): Promise<ComputeCounterAllowanceResult> {
    const decimal = Number(
      await this.erc20ContractService.getDecimal(input.account.networkId, input.currencyAddress)
    );

    // Prefer the exact on-chain pull (no float buffer, zero residual). Fall back
    // to the human snapshot + toWei drift buffer only when no exact value exists.
    // A negative or non-numeric requirement means the caller's own calculation was wrong - most
    // often a self-refinance netting the parent payoff against a smaller new loan. toWei clamps
    // such a value to 0n, which then reads as "nothing to approve" and lets the accept fail later
    // for want of an allowance nobody requested. Surface it here instead of approving zero.
    // Exactly 0 stays valid: the cancel, release and reduce-allowance callers pass it to shrink
    // the allowance back to whatever the other active offers still need.
    const hasExactWei = typeof input.counterAmountWithFeesWei === 'bigint' && input.counterAmountWithFeesWei > 0n;
    if (!hasExactWei && !(Number.isFinite(input.counterAmountWithFeesHuman) && input.counterAmountWithFeesHuman >= 0)) {
      throw new Error('Could not work out the approval needed for this offer. Please try again.');
    }
    const counterAllowanceWei = hasExactWei
      ? input.counterAmountWithFeesWei as bigint
      : this.toWei(input.counterAmountWithFeesHuman, decimal);

    // activeCounterSum still comes from the backend (DB-of-record for committed
    // bid amounts). contractAllowance is read directly on-chain via wagmi so we
    // avoid the wei → human-string → Number → wei round-trip, which loses sub-
    // unit precision and can make a `decrease` overshoot the current allowance
    // and revert with "ERC20: decreased allowance below zero".
    const spenderKind: AllowanceSpender = input.spender ?? 'lending';
    const spender = getSpenderAddress(input.account.chainId, spenderKind);
    const [{ activeCounterSum }, contractAllowanceRaw] = await Promise.all([
      this.checkAllowance(input.currencyId, input.excludeId, spenderKind),
      this.erc20ContractService.getAllowance(
        input.account.networkId,
        input.currencyAddress,
        input.account.walletAddress,
        spender
      )
    ]);

    const activeCounterSumWei = this.toWei(Number(activeCounterSum || 0), decimal);
    const contractAllowanceWei = this.toBigInt(contractAllowanceRaw);

    // Round the required allowance UP to a whole token (e.g. 1.25 -> 2) so the
    // on-chain allowance always clears what the contract pulls in transferFrom,
    // with comfortable headroom the tiny float buffer alone can't guarantee on
    // larger loans. action/deltaWei below are computed against this ceiled value,
    // so every caller's increaseAllowance/decreaseAllowance reaches a whole-token
    // allowance with no call-site changes.
    const { action, deltaWei } = this.resolveAllowanceAction(
      contractAllowanceWei,
      activeCounterSumWei + counterAllowanceWei,
      decimal
    );

    return {
      // Sent to the backend as `allowance_amount`, which rejects values with >2 decimal
      // places. Float drift (e.g. 73.22999999999999) would 400, so round to 2dp here.
      //
      // This is NOT record-only: the backend sums it across the lender's active counters
      // into `activeCounterSum`, which sets what every further counter has to approve and
      // what the decrease sweep trims down to. So it has to describe the same figure the
      // approval was built from - reporting the human fallback while an exact wei amount
      // was used would carry the difference into every sibling counter and leave the sweep
      // comparing the on-chain allowance against a requirement that was never approved.
      allowanceAmount: hasExactWei
        ? this.weiToRecordedAmount(counterAllowanceWei, decimal)
        : Number.isFinite(input.counterAmountWithFeesHuman)
          ? Number(input.counterAmountWithFeesHuman.toFixed(2))
          : input.counterAmountWithFeesHuman,
      action,
      deltaWei,
      spenderAddress: spender
    };
  }

  /**
   * Sizes the allowance for a one-off payment to a contract no open commitment draws on - an NFT
   * collection (custody fee, lazy-mint purchase) or the syndicate pool.
   *
   * Nothing else waits on that allowance, so this payment is the whole requirement. An allowance
   * left over from an earlier attempt is counted instead of stacked on top of, and a surplus is
   * trimmed back, so a retried payment never leaves more approved than it pulls.
   */
  async computeExactAllowance(input: ComputeExactAllowanceInput): Promise<ComputeExactAllowanceResult> {
    // Zero or a failed calculation would read as "nothing to approve" and let the payment fail
    // later for want of an allowance nobody requested.
    const requiredWei = this.toBigInt(input.requiredWei);
    if (requiredWei <= 0n) {
      throw new Error('Could not work out the approval needed for this payment. Please try again.');
    }

    const [decimalRaw, allowanceRaw] = await Promise.all([
      this.erc20ContractService.getDecimal(input.account.networkId, input.currencyAddress),
      this.erc20ContractService.getAllowance(
        input.account.networkId,
        input.currencyAddress,
        input.account.walletAddress,
        input.spenderAddress
      )
    ]);

    return {
      ...this.resolveAllowanceAction(this.toBigInt(allowanceRaw), requiredWei, Number(decimalRaw)),
      spenderAddress: input.spenderAddress
    };
  }

  /**
   * Compares the on-chain allowance with what must be approved and says how to get there.
   *
   * The requirement is rounded UP to a whole token (e.g. 1.25 -> 2) so the allowance always
   * clears what the contract pulls in transferFrom, with headroom a float buffer alone can't
   * guarantee on larger amounts. Callers send the returned delta as increaseAllowance or
   * decreaseAllowance and land on that whole-token figure.
   */
  private resolveAllowanceAction(
    currentWei: bigint,
    rawRequiredWei: bigint,
    decimal: number
  ): { action: AllowanceAction; deltaWei: bigint } {
    const safeDecimalForCeil = Number.isInteger(decimal) && decimal >= 0 ? decimal : 18;
    const tokenUnit = 10n ** BigInt(safeDecimalForCeil);
    const requiredWei = ((rawRequiredWei + tokenUnit - 1n) / tokenUnit) * tokenUnit;

    // requiredWei is positive for every caller, so 'none' can only mean the existing allowance
    // already covers it - it can never be reached by a requirement that failed to compute.
    if (currentWei < requiredWei) {
      return { action: 'increase', deltaWei: requiredWei - currentWei };
    }
    if (currentWei > requiredWei) {
      // Capped at the actual on-chain allowance: if anything decreased it between the read and
      // this signing (a cron, another tab), this prevents the "below zero" revert and just
      // no-ops down to 0.
      const surplus = currentWei - requiredWei;
      return { action: 'decrease', deltaWei: surplus > currentWei ? currentWei : surplus };
    }
    return { action: 'none', deltaWei: 0n };
  }

  /**
   * A wei requirement expressed in whole currency units, for the figure recorded against
   * a counter. Rounded UP to the 2 decimal places the API accepts, so the record never
   * states less than was actually required.
   */
  private weiToRecordedAmount(valueWei: bigint, decimal: number): number {
    const safeDecimal = Number.isInteger(decimal) && decimal >= 0 ? decimal : 18;
    if (safeDecimal <= 2) return Number(valueWei) / 10 ** safeDecimal;
    // Scale down to hundredths, rounding up, so truncation can never under-report.
    const perHundredth = 10n ** BigInt(safeDecimal - 2);
    return Number((valueWei + perHundredth - 1n) / perHundredth) / 100;
  }

  private toBigInt(value: unknown): bigint {
    if (typeof value === 'bigint') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return BigInt(Math.trunc(value));
    if (typeof value === 'string' && value.length > 0) {
      try { return BigInt(value); } catch { return 0n; }
    }
    return 0n;
  }

  private toWei(humanAmount: number, decimal: number): bigint {
    if (!Number.isFinite(humanAmount) || humanAmount <= 0) return 0n;
    const safeDecimal = Number.isInteger(decimal) && decimal >= 0 ? decimal : 18;
    const fixed = humanAmount.toFixed(safeDecimal);
    const base = parseUnits(fixed, safeDecimal);
    // IEEE-754 float precision drift: e.g. `1111 + 12.11` in JS double is
    // 1123.10999999999990449634…, while the contract computes 1123.11 exactly with
    // integer math, leaving the on-chain allowance ~1e5 wei short and reverting
    // transferFrom with "ERC20: transfer amount exceeds allowance". A small buffer
    // (0.001 of one token unit, scaled by decimals) absorbs that drift across the
    // full range of practical amounts (sub-billion tokens) without materially
    // over-approving. Smart decrease logic trims the leftover later.
    const buffer = 10n ** BigInt(Math.max(0, safeDecimal - 3));
    return base + buffer;
  }
}
